import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, ExternalLink, FileWarning } from "lucide-react"

/**
 * Anomalien-Bereich im Review (#24).
 *
 * Gewünscht von Paul am 24.08.2026 aus der Triage zu #21: „Das müsste ein
 * neues Feature sein 'Anomalien'; ggf. im Review-Tab neben Mails und
 * Projekten. Dort sollte z. B. auch sein, wenn ein Arbeitsbericht an einem
 * Kontakt hängt und nicht an einem Projekt."
 *
 * 🔴 Reine Anzeige — hier wird nichts nach HERO geschrieben. Der naheliegende
 * Automatismus „Kunde hat genau einen Vorgang, also dranhängen" trägt nicht:
 * Erich Muster Beispiel Immobilien hat 31 Vorgänge. Ein falsch zugeordneter Beleg
 * ist schlimmer als ein unzugeordneter. Also eine Liste für Menschen.
 */

type Beleg = {
  id: number
  tenant_slug: string
  nr: string | null
  doc_type: string | null
  type_name: string | null
  status_name: string | null
  total_gross: number | null
  created: string | null
  link_view: string | null
  project_nr: string | null
  contact_id: number | null
  contact_name: string | null
  anzahl_vorgaenge: number
  ist_bericht: boolean
  /** Warum „in HERO öffnen" fehlt — null, wenn ein Link da ist. */
  kein_link_grund: string | null
}

type Antwort = {
  umhaengbar: Beleg[]
  ohne_vorgang: Beleg[]
  summe_umhaengbar: number
  summe_ohne_vorgang: number
  total: number
}

const euro = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("de-DE", { style: "currency", currency: "EUR" })

function Gruppe({
  titel, hinweis, belege, summe,
}: {
  titel: string
  hinweis: string
  belege: Beleg[]
  summe: number
}) {
  if (belege.length === 0) return null
  return (
    <section style={{ marginBottom: 26 }}>
      <h3 style={{
        fontSize: 14, fontWeight: 600, marginBottom: 2,
        display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap",
      }}>
        {titel}
        <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-muted)" }}>
          {belege.length} · {euro(summe)}
        </span>
      </h3>
      <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 8px" }}>{hinweis}</p>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {belege.map((b) => (
          <li
            key={`${b.tenant_slug}-${b.id}`}
            style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "8px 4px", borderBottom: "1px solid var(--border-color)",
            }}
          >
            {/* Ein Bericht ist der dringendere Fall: aus genau dieser
                Konstellation kamen im August die 120 Fehlversuche des
                Doc-Status-Switch. */}
            {b.ist_bericht
              ? <FileWarning size={14} style={{ marginTop: 2, flexShrink: 0, color: "#B45309" }} />
              : <AlertTriangle size={14} style={{ marginTop: 2, flexShrink: 0, color: "#94a3b8" }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "baseline" }}>
                <strong>{b.nr || `#${b.id}`}</strong>
                <span style={{ color: "var(--text-muted)" }}>{b.type_name || b.doc_type}</span>
                {b.status_name && (
                  <span style={{
                    fontSize: 11.5, padding: "0 6px", borderRadius: 999,
                    background: "rgba(44,44,42,0.06)", color: "var(--text-muted)",
                  }}>{b.status_name}</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                {b.contact_name || "(ohne Kontakt)"}
                {b.anzahl_vorgaenge > 0 && (
                  <> · {b.anzahl_vorgaenge} {b.anzahl_vorgaenge === 1 ? "Vorgang" : "Vorgänge"} beim Kunden</>
                )}
                {b.project_nr && <> · hängt an {b.project_nr}</>}
              </div>
            </div>
            <div style={{ flexShrink: 0, textAlign: "right" }}>
              <div style={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{euro(b.total_gross)}</div>
              {b.link_view ? (
                <a
                  href={b.link_view}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 11.5, color: "var(--accent-color)", display: "inline-flex", alignItems: "center", gap: 3 }}
                >
                  in HERO öffnen <ExternalLink size={10} />
                </a>
              ) : (
                /* 🔴 Nicht stumm weglassen: „bei manchen geht es, bei anderen
                   nicht" sieht nach einem Fehler aus. HERO liefert für
                   Entwürfe schlicht keine Ansicht — das gehört dahin. */
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {b.kein_link_grund || "kein Link"}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function AnomalienTab({ tenant }: { tenant: string }) {
  // 🔑 Gelöschte Belege sind gar nicht mehr in der Antwort — sie sind keine
  // Arbeit und deshalb auch kein Filter. Hier geht es nur um die übrigen
  // Null-Beträge: Entwürfe, die noch keinen Betrag haben.
  const [nurMitBetrag, setNurMitBetrag] = useState(false)

  const q = useQuery<Antwort>({
    queryKey: ["anomalien", tenant],
    queryFn: async () => {
      const p = tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""
      const r = await fetch(`/api/v1/anomalien${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60,
  })

  if (q.isLoading) {
    return <div style={{ padding: 20, fontSize: 13, color: "var(--text-muted)" }}>Lädt …</div>
  }
  if (q.isError) {
    return (
      <div style={{ padding: 20, fontSize: 13, color: "var(--destructive)" }}>
        Anomalien nicht ladbar: {String(q.error)}
      </div>
    )
  }
  const roh = q.data!
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const d = ((): Antwort => {
    if (!nurMitBetrag) return roh
    const mit = (xs: Beleg[]) => xs.filter((b) => (b.total_gross || 0) > 0)
    const u = mit(roh.umhaengbar)
    const o = mit(roh.ohne_vorgang)
    return {
      umhaengbar: u,
      ohne_vorgang: o,
      summe_umhaengbar: u.reduce((s, b) => s + (b.total_gross || 0), 0),
      summe_ohne_vorgang: o.reduce((s, b) => s + (b.total_gross || 0), 0),
      total: u.length + o.length,
    }
  })()
  const ohneBetrag = roh.total - (roh.umhaengbar.filter((b) => (b.total_gross || 0) > 0).length
    + roh.ohne_vorgang.filter((b) => (b.total_gross || 0) > 0).length)

  if (roh.total === 0) {
    return (
      <div style={{ padding: 20, fontSize: 13, color: "var(--text-muted)" }}>
        Nichts gefunden — jeder Beleg hängt an einem Vorgang.
      </div>
    )
  }

  return (
    <div style={{ padding: "16px 20px", overflowY: "auto" }}>
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "0 0 10px", maxWidth: 640 }}>
        Belege und Berichte, die an der Kontakt-Hülle hängen statt an einem
        Vorgang. Umgehängt wird in HERO — das Portal zeigt hier nur, wo etwas
        fehlt. Gelöschte sind nicht dabei.
      </p>
      {ohneBetrag > 0 && (
        <button
          type="button"
          onClick={() => setNurMitBetrag(!nurMitBetrag)}
          title="Entwürfe haben noch keinen Betrag"
          style={{
            fontSize: 12, padding: "4px 11px", borderRadius: 999, cursor: "pointer",
            background: nurMitBetrag ? "#EEF2FF" : "transparent",
            color: nurMitBetrag ? "#3C3489" : "var(--text-muted)",
            border: `1px solid ${nurMitBetrag ? "#C7D2FE" : "var(--border-color)"}`,
            fontWeight: nurMitBetrag ? 600 : 500, marginBottom: 16,
          }}
        >
          Nur mit Betrag <span style={{ opacity: 0.75 }}>· {ohneBetrag} ohne</span>
        </button>
      )}
      <Gruppe
        titel="Umhängbar"
        hinweis="Der Kunde hat Vorgänge — der Beleg gehört an einen davon. Welchen, ist nicht erratbar, deshalb steht das hier und passiert nicht von selbst."
        belege={d.umhaengbar}
        summe={d.summe_umhaengbar}
      />
      <Gruppe
        titel="Kunde hat gar keinen Vorgang"
        hinweis="Hier muss erst ein Vorgang entstehen, bevor der Beleg irgendwo hin kann."
        belege={d.ohne_vorgang}
        summe={d.summe_ohne_vorgang}
      />
    </div>
  )
}
